/**
 * Security Constants (Frontend Copy)
 *
 * SYNC WITH: server/constants/security.ts
 *
 * This file must stay in sync with the backend version.
 * A sync test in server/__tests__/security-sync.test.ts verifies equality.
 * If you update this file, update the backend version too.
 */

/**
 * Comprehensive list of executable file extensions that should be blocked
 * Based on security best practices and common malware vectors
 */
export const BLOCKED_EXECUTABLE_EXTENSIONS: readonly string[] = [
  // Windows executables
  'exe', 'dll', 'bat', 'cmd', 'com', 'scr', 'pif', 'vbs', 'vbe', 'js', 'jse',
  'wsf', 'wsh', 'msi', 'msp', 'scf', 'lnk', 'inf', 'reg',
  // PowerShell
  'ps1', 'ps1xml', 'ps2', 'ps2xml', 'psc1', 'psc2',
  // macOS executables
  'app', 'dmg', 'pkg', 'command',
  // Linux executables
  'sh', 'bash', 'run', 'elf', 'bin',
  // Mobile executables
  'apk', 'ipa', 'dex',
  // Java executables
  'jar', 'class', 'war', 'ear',
  // Other scripting/compiled
  'py', 'rb', 'pl', 'php', 'cgi',
  // Additional coverage
  'ts', 'tsx', 'mjs', 'cjs', // Modern JavaScript/TypeScript (can be executed)
  'wasm', // WebAssembly
  'so', 'dylib', 'ocx', 'sys', // System libraries
  // Archive files (blocked to prevent executable smuggling)
  'zip', 'tar', 'gz', 'tgz', 'bz2', 'tbz2', 'xz', 'txz',
  '7z', 'rar', 'arj', 'cab', 'lz', 'lzh', 'lzma', 'z',
  'iso', // Disk images (dmg and pkg already listed above in macOS section)
];

/**
 * Check if file extension is a blocked executable type
 * @param filename - The filename to check (e.g., "document.pdf" or "malware.exe")
 * @returns true if the file extension is blocked, false otherwise
 */
export function isBlockedExecutable(filename: string): boolean {
  if (!filename || typeof filename !== 'string') {
    return false; // Defensive: invalid input is not executable
  }

  // Optimized: Use lastIndexOf to avoid split array allocation
  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
    return false; // No extension or dot at end
  }

  const extension = filename.substring(lastDotIndex + 1).toLowerCase();
  return BLOCKED_EXECUTABLE_EXTENSIONS.includes(extension);
}

/**
 * Standardized error message for blocked files
 * Use this exact message across frontend and backend for consistency
 * @deprecated Use getBlockedFileErrorMessage() instead for file-type-specific messages
 */
export const BLOCKED_FILE_ERROR_MESSAGE =
  'This file type is not allowed for security reasons. Please upload documents, images, videos, or audio files instead.';

/**
 * Generate a file-type-specific error message for blocked files
 * @param filename - The filename to extract extension from (e.g., "malware.exe")
 * @returns Error message with file type included (e.g., "EXE files are not allowed...")
 */
export function getBlockedFileErrorMessage(filename: string): string {
  if (!filename || typeof filename !== 'string') {
    return BLOCKED_FILE_ERROR_MESSAGE;
  }

  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1 || lastDotIndex === filename.length - 1) {
    return BLOCKED_FILE_ERROR_MESSAGE;
  }

  const extension = filename.substring(lastDotIndex + 1).toUpperCase();
  return `${extension} files are not allowed for security reasons. Please upload documents, images, videos, or audio files instead.`;
}
